import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import type { Issue, Job } from "@/data/types";
import { toUiBuildReviewEvent } from "@/mappers/build-review.mapper";
import { toUiIssue } from "@/mappers/issue.mapper";
import { toUiJob } from "@/mappers/job.mapper";
import type { BuildReviewEventRow } from "@/services/build-review.service";
import type { IssueRow } from "@/services/issue.service";
import type { JobRow } from "@/services/job.service";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export type IntakeReviewLane =
  | "needs-review"
  | "on-hold-sales"
  | "revision-required"
  | "awaiting-sales"
  | "awaiting-signature"
  | "resubmitted"
  | "final-review";

export type IntakeReviewAgeBucket = "overdue" | "at-risk" | "watch" | "fresh";

export interface IntakeReviewJobEntry {
  job: Job;
  buildReviewEvents: BuildReviewEvent[];
  openIssues: Issue[];
  buildReviewStatus: string;
  lane: IntakeReviewLane;
  ageHours: number;
  ageBucket: IntakeReviewAgeBucket;
  openIssueCount: number;
  sortAt: string;
}

export interface IntakeReviewSummary {
  pending: number;
  onHold: number;
  awaitingSignature: number;
  overdue: number;
  atRisk: number;
  watch: number;
  fresh: number;
}

export interface IntakeReviewJobsPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  lane?: IntakeReviewLane | "all";
  bucket?: IntakeReviewAgeBucket | "all";
  refreshToken?: number;
}

type IntakeReviewJobViewRow = {
  organization_id?: string | null;
  id: string;
  source_meta?: Job["sourceMeta"] | null;
  current_stage?: string | null;
  title?: string | null;
  job_type?: string | null;
  job_number?: string | null;
  customer_id?: string | null;
  contractor_id?: string | null;
  crew_id?: string | null;
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  material_readiness?: string | null;
  permit_required?: boolean | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_address_line1?: string | null;
  customer_address_line2?: string | null;
  customer_city?: string | null;
  customer_state?: string | null;
  customer_postal_code?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
  branch?: string | null;
  region?: string | null;
  assigned_crew?: string | null;
  assigned_subcontractor?: string | null;
  assignment_type?: string | null;
  permit_status?: string | null;
  qa_submitted?: boolean | null;
  build_review_events?: BuildReviewEventRow[] | null;
  open_issues?: IssueRow[] | null;
  open_issue_count?: number | null;
  build_review_status?: string | null;
  review_lane?: IntakeReviewLane | null;
  review_entered_at?: string | null;
  age_hours?: number | string | null;
  age_bucket?: IntakeReviewAgeBucket | null;
  sort_at?: string | null;
};

type IntakeReviewSummaryViewRow = {
  pending?: number | null;
  on_hold?: number | null;
  awaiting_signature?: number | null;
  overdue?: number | null;
  at_risk?: number | null;
  watch?: number | null;
  fresh?: number | null;
};

const JOB_COLUMNS = [
  "organization_id",
  "id",
  "source_meta",
  "current_stage",
  "title",
  "job_type",
  "job_number",
  "customer_id",
  "contractor_id",
  "crew_id",
  "scheduled_start_at",
  "scheduled_end_at",
  "address_line1",
  "address_line2",
  "city",
  "state",
  "postal_code",
  "material_readiness",
  "permit_required",
  "notes",
  "metadata",
  "created_at",
  "updated_at",
  "customer_first_name",
  "customer_last_name",
  "customer_address_line1",
  "customer_address_line2",
  "customer_city",
  "customer_state",
  "customer_postal_code",
  "customer_name",
  "project_type",
  "branch",
  "region",
  "assigned_crew",
  "assigned_subcontractor",
  "assignment_type",
  "permit_status",
  "qa_submitted",
  "build_review_events",
  "open_issues",
  "open_issue_count",
  "build_review_status",
  "review_lane",
  "review_entered_at",
  "age_hours",
  "age_bucket",
  "sort_at",
].join(", ");

function num(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

function mapJob(row: IntakeReviewJobViewRow): Job {
  const metadata = {
    ...(row.metadata ?? {}),
    customerName: row.customer_name ?? (row.metadata?.customerName as string | undefined),
    projectType: row.project_type ?? (row.metadata?.projectType as string | undefined),
    branch: row.branch ?? (row.metadata?.branch as string | undefined),
    region: row.region ?? (row.metadata?.region as string | undefined),
    assignedCrew: row.assigned_crew ?? (row.metadata?.assignedCrew as string | undefined),
    assignedSubcontractor: row.assigned_subcontractor ?? (row.metadata?.assignedSubcontractor as string | undefined),
    assignmentType: row.assignment_type ?? (row.metadata?.assignmentType as string | undefined),
    permitStatus: row.permit_status ?? (row.metadata?.permitStatus as string | undefined),
    qaSubmitted: row.qa_submitted ?? (row.metadata?.qaSubmitted as boolean | undefined),
  };

  return toUiJob({
    id: row.id,
    organization_id: row.organization_id ?? null,
    source_meta: row.source_meta ?? null,
    current_stage: row.current_stage ?? null,
    title: row.title ?? null,
    job_type: row.job_type ?? row.project_type ?? null,
    job_number: row.job_number ?? null,
    customer_id: row.customer_id ?? null,
    contractor_id: row.contractor_id ?? null,
    crew_id: row.crew_id ?? null,
    scheduled_start_at: row.scheduled_start_at ?? null,
    scheduled_end_at: row.scheduled_end_at ?? null,
    address_line1: row.address_line1 ?? null,
    address_line2: row.address_line2 ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    postal_code: row.postal_code ?? null,
    material_readiness: row.material_readiness ?? null,
    permit_required: row.permit_required ?? undefined,
    notes: row.notes ?? null,
    metadata,
    customers: {
      first_name: row.customer_first_name ?? null,
      last_name: row.customer_last_name ?? null,
      address_line1: row.customer_address_line1 ?? null,
      address_line2: row.customer_address_line2 ?? null,
      city: row.customer_city ?? null,
      state: row.customer_state ?? null,
      postal_code: row.customer_postal_code ?? null,
    },
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? row.created_at ?? new Date().toISOString(),
  } as JobRow);
}

function mapIssue(row: IssueRow, job: Job): Issue {
  const issue = toUiIssue(row);
  return {
    ...issue,
    customerName: issue.customerName || job.customerName,
    jobNumber: issue.jobNumber || job.jobNumber,
  };
}

function mapIntakeReviewJob(row: IntakeReviewJobViewRow): IntakeReviewJobEntry {
  const job = mapJob(row);
  const buildReviewEvents = (row.build_review_events ?? []).map((event) => toUiBuildReviewEvent(event));
  const openIssues = (row.open_issues ?? []).map((issue) => mapIssue(issue, job));
  return {
    job,
    buildReviewEvents,
    openIssues,
    buildReviewStatus: row.build_review_status ?? "Needs Review",
    lane: row.review_lane ?? "needs-review",
    ageHours: num(row.age_hours),
    ageBucket: row.age_bucket ?? "fresh",
    openIssueCount: row.open_issue_count ?? openIssues.length,
    sortAt: row.sort_at ?? row.updated_at ?? row.created_at ?? new Date().toISOString(),
  };
}

function mapSummary(row: IntakeReviewSummaryViewRow | null | undefined): IntakeReviewSummary {
  return {
    pending: num(row?.pending),
    onHold: num(row?.on_hold),
    awaitingSignature: num(row?.awaiting_signature),
    overdue: num(row?.overdue),
    atRisk: num(row?.at_risk),
    watch: num(row?.watch),
    fresh: num(row?.fresh),
  };
}

export const IntakeReviewPageService = {
  async getJobsPage(query: IntakeReviewJobsPageQuery): Promise<PageResponse<IntakeReviewJobEntry>> {
    const key = `intake-review.jobs-page.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        let request = supabase
          .from("v_intake_review_jobs_ui")
          .select(JOB_COLUMNS, { count: "exact" })
          .eq("is_pending_review", true)
          .order("review_entered_at", { ascending: true })
          .range(from, to);

        const search = query.search?.trim();
        if (search) {
          const pattern = `%${search.replace(/[%_]/g, "").replace(/,/g, " ")}%`;
          request = request.or(`customer_name.ilike.${pattern},job_number.ilike.${pattern},city.ilike.${pattern},project_type.ilike.${pattern}`);
        }
        if (query.lane && query.lane !== "all") request = request.eq("review_lane", query.lane);
        if (query.bucket && query.bucket !== "all") request = request.eq("age_bucket", query.bucket);

        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as IntakeReviewJobViewRow[]).map(mapIntakeReviewJob), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("IntakeReviewPageService.getJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: IntakeReviewSummary | null; error: unknown | null }> {
    const key = `intake-review.summary.${hashFilters({ refreshToken })}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_intake_review_summary")
          .select("pending, on_hold, awaiting_signature, overdue, at_risk, watch, fresh")
          .maybeSingle();
        if (error) throw error;
        return { data: mapSummary(data as IntakeReviewSummaryViewRow | null), error: null };
      });
    } catch (error) {
      console.warn("IntakeReviewPageService.getSummary failed:", error);
      return { data: null, error };
    }
  },
};
